import type { CoverageRow, CoverageSort, SavedView } from '@fruuit/shared'
import {
  Button,
  Callout,
  Card,
  Dialog,
  DialogCancel,
  EmptyState,
  Input,
  useDialog,
  useToast,
} from '@fruuit/ui'
import { Download, Info, Save, SearchX } from 'lucide-react'
import { type FormEvent, useCallback, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { errorMessage, isApiError } from '../../api/client'
import {
  downloadCoverageCsv,
  FILTER_KEYS,
  type FilterState,
  filtersFromParams,
  savedViewFilters,
  useCoverage,
  useCoverageSummary,
  useSavedViews,
  useSaveView,
} from './api'
import { CoverageFilters } from './CoverageFilters'
import { CoverageTable } from './CoverageTable'

const PAGE_SIZE = 50
const SORTS: readonly CoverageSort[] = ['company', 'portfolio', 'finding', 'next_review', 'as_of']

/** Filters, sort and page live in the URL, so views can be shared and reloaded. */
export function useCoverageParams() {
  const [params, setParams] = useSearchParams()
  const filters = useMemo(() => filtersFromParams(params), [params])
  const sortParam = params.get('sort') as CoverageSort | null
  const sort: CoverageSort = sortParam && SORTS.includes(sortParam) ? sortParam : 'company'
  const dir: 'asc' | 'desc' = params.get('dir') === 'desc' ? 'desc' : 'asc'
  const [cursors, setCursors] = useState<string[]>([])

  const update = useCallback(
    (patch: Record<string, string | undefined>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          for (const [key, value] of Object.entries(patch)) {
            if (value) next.set(key, value)
            else next.delete(key)
          }
          return next
        },
        { replace: true },
      )
      setCursors([])
    },
    [setParams],
  )

  const setFilters = useCallback(
    (next: FilterState) => {
      const patch: Record<string, string | undefined> = {}
      for (const key of FILTER_KEYS) patch[key] = next[key]
      update(patch)
    },
    [update],
  )

  return {
    params,
    filters,
    setFilters,
    sort,
    dir,
    setSort: (s: CoverageSort, d: 'asc' | 'desc') => update({ sort: s, dir: d }),
    cursor: cursors[cursors.length - 1] ?? null,
    page: cursors.length,
    next: (cursor: string) => setCursors((list) => [...list, cursor]),
    previous: () => setCursors((list) => list.slice(0, -1)),
  }
}

export interface CoverageRegisterProps {
  /** The staff client context; null for the investor's own org. */
  org: string | null
  base: '/app' | '/workspace'
  staff?: boolean
  onResolve?: (row: CoverageRow) => void
}

/** Toolbar + summary + table + info line: the S2 register shared by both areas. */
export function CoverageRegister({ org, base, staff = false, onResolve }: CoverageRegisterProps) {
  const state = useCoverageParams()
  const { toast } = useToast()
  const summary = useCoverageSummary(org, state.filters)
  const coverage = useCoverage({
    org,
    filters: state.filters,
    sort: state.sort,
    dir: state.dir,
    cursor: state.cursor,
    limit: PAGE_SIZE,
  })
  const views = useSavedViews()
  const savedViews = views.data?.items
  const activeView = savedViews?.find((v) => sameFilters(v.filters, state.filters))
  const saveDialog = useDialog()
  const [exporting, setExporting] = useState(false)
  const linkQuery = org ? `?org=${encodeURIComponent(org)}` : ''

  const chooseView = (view: SavedView | null) => {
    if (view) state.setFilters(view.filters as FilterState)
  }

  async function exportCsv() {
    setExporting(true)
    try {
      await downloadCoverageCsv(org, state.filters)
    } catch (error) {
      toast({ tone: 'error', title: 'Export not downloaded', message: errorMessage(error) })
    } finally {
      setExporting(false)
    }
  }

  const totals = summary.data?.totals
  const page = coverage.data
  const error = coverage.error ?? summary.error

  return (
    <div className="space-y-4">
      <Card padding="md" className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-ink" aria-live="polite">
            {totals ? (
              <>
                <b className="tabular-nums">{totals.holdings}</b> holdings ·{' '}
                <b className="tabular-nums">{totals.mapped_companies}</b> mapped companies ·{' '}
                <b className="tabular-nums">{totals.mapping_exceptions}</b> mapping exceptions
                <span className="mt-0.5 block text-xs text-sage">Latest recorded portfolio positions</span>
              </>
            ) : (
              <span className="text-sage">Loading coverage…</span>
            )}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              icon={<Save />}
              onClick={() => saveDialog.open()}
              disabled={views.isError}
            >
              Save this view
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={<Download />}
              loading={exporting}
              onClick={exportCsv}
            >
              Export filtered CSV
            </Button>
          </div>
        </div>
        <CoverageFilters
          filters={state.filters}
          summary={summary.data}
          onChange={state.setFilters}
          {...(savedViews ? { savedViews, onChooseView: chooseView } : {})}
          {...(activeView ? { activeViewId: activeView.id } : {})}
        />
        {error ? (
          <Callout tone="danger" role="alert" title="Coverage could not be loaded">
            {errorMessage(error)}
          </Callout>
        ) : null}
        <CoverageTable
          rows={page?.items ?? []}
          loading={coverage.isPending}
          base={base}
          staff={staff}
          linkQuery={linkQuery}
          sort={state.sort}
          dir={state.dir}
          onSortChange={state.setSort}
          {...(onResolve ? { onResolve } : {})}
          pagination={{
            hasNext: Boolean(page?.next_cursor),
            hasPrevious: state.page > 0,
            onNext: () => page?.next_cursor && state.next(page.next_cursor),
            onPrevious: state.previous,
            summary: page
              ? `Showing ${page.total === 0 ? 0 : state.page * PAGE_SIZE + 1}–${state.page * PAGE_SIZE + page.items.length} of ${page.total}`
              : '',
          }}
          empty={
            <EmptyState
              variant="plain"
              icon={<SearchX />}
              title="No holdings match these filters"
              description="Reset or broaden your selection."
              action={
                <Button variant="secondary" size="sm" onClick={() => state.setFilters({})}>
                  Reset filters
                </Button>
              }
            />
          }
        />
      </Card>
      <p className="flex flex-wrap items-start gap-2 text-sm leading-relaxed text-sage">
        <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-teal-400" />
        <span className="min-w-0 flex-1">
          Findings reflect published Fruuit research. Client actions are recorded separately and
          do not change the research conclusion. Multiple securities remain distinct holdings.
        </span>
        {base === '/app' ? (
          <Link to="/app/methodology" className="font-semibold text-teal hover:underline">
            Status definitions →
          </Link>
        ) : null}
      </p>
      <SaveViewDialog
        open={saveDialog.isOpen}
        onClose={saveDialog.close}
        filters={state.filters}
        onSaved={(view) => toast({ tone: 'success', message: `Saved “${view.name}”.` })}
      />
    </div>
  )
}

function sameFilters(a: Record<string, string>, b: FilterState): boolean {
  const left = savedViewFilters(a as FilterState)
  const right = savedViewFilters(b)
  const keys = new Set([...Object.keys(left), ...Object.keys(right)])
  if (keys.size === 0) return false
  for (const key of keys) if (left[key] !== right[key]) return false
  return true
}

function SaveViewDialog({
  open,
  onClose,
  filters,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  filters: FilterState
  onSaved: (view: SavedView) => void
}) {
  const [name, setName] = useState('')
  const save = useSaveView()
  const fieldError = isApiError(save.error, 'validation_failed')
    ? (save.error.fieldErrors.name ?? save.error.message)
    : isApiError(save.error, 'duplicate')
      ? 'You already have a view with this name.'
      : undefined

  async function submit(event: FormEvent) {
    event.preventDefault()
    try {
      const view = await save.mutateAsync({ name, filters: savedViewFilters(filters) })
      setName('')
      onClose()
      onSaved(view)
    } catch {
      // Shown below.
    }
  }

  const unsupported = Boolean(filters.label || filters.mapping)
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Save this view"
      description="Saved views are private to your account."
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form="save-view-form" loading={save.isPending}>
            Save view
          </Button>
        </>
      }
    >
      <form id="save-view-form" onSubmit={submit} className="space-y-4">
        <Input
          label="View name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          minLength={2}
          maxLength={80}
          required
          error={fieldError}
        />
        {unsupported ? (
          <Callout tone="info">
            Review-status and mapping filters are not stored in saved views.
          </Callout>
        ) : null}
        {save.error && !fieldError ? (
          <Callout tone="danger" role="alert">
            {errorMessage(save.error)}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  )
}
